import { Availability } from '../domain/Availability'
import { BookingDetails } from '../domain/BookingDetails'
import { BookingRequest } from '../domain/BookingRequest'
import { BookingRequestFactory } from '../domain/BookingRequestFactory'
import { BookingSlot } from '../domain/BookingSlot'
import { bookingPaymentTerminologyForVenueId } from '../domain/BookingPaymentTerminology'
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms'
import { FormStep } from '../booking-form/booking-form.component'
import { HttpErrorResponse } from '@angular/common/http'
import { NGBDateTimeAdapter } from '../utilities/NgbDateTimeAdapter'
import { NgbDateNativeAdapter } from '@ng-bootstrap/ng-bootstrap'
import { PaymentSession, PaymentSessionValidationError } from '../services/PaymentSession'
import { PaymentsService } from '../services/payments.service'
import { Venue } from '../domain/Venue'
import DineroFactory, { Currency, Dinero } from 'dinero.js'
import { LoggingService } from '../services/logging.service'
import { v4 as uuidv4 } from 'uuid'
import { PaymentIntent } from '../domain/PaymentIntent'

@Component({
    selector: 'app-deposit-form',
    styleUrls: ['./deposit-form.component.sass'],
    templateUrl: './deposit-form.component.html',
})
export class DepositFormComponent implements OnInit, OnDestroy {

    @Input() showTitle = false
    @Input() showBackButton = false
    @Input() venue?: Venue
    @Input() bookingDetails?: BookingDetails
    @Input() customerDepositAmount: number | null = null
    @Input() customerDepositIsPerCover = true
    @Input() utmSource?: string
    @Input() utmMedium?: string
    @Input() utmCampaign?: string
    @Output() backSelected = new EventEmitter<void>()
    @Output() paymentStarted = new EventEmitter<string>()
    @Output() paymentFailed = new EventEmitter<void>()
    @Output() bookSelected = new EventEmitter<void>()
    amount!: Dinero
    refundCutOffHours: number | null = null
    refundCutOffTime: Date | null = null
    formError: string | null = null
    bookingSlotUnavailableWarning: string | null = null
    isSaving = false
    hoursPluralMapping = {
        '=0': '# hours',
        '=1': '# hour',
        other: '# hours',
    }
    cardDetailsAreValid = false
    paymentFormIsReady = false
    walletPaymentAvailable = false
    billingEmailAddressControl = new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
    })
    private paymentIntent: PaymentIntent | null = null
    private paymentSession: PaymentSession | null = null
    private destroyed = false
    private ngbDateAdapter = new NgbDateNativeAdapter()
    private ngbTimeAdapter = new NGBDateTimeAdapter()

    get isRyftPayment(): boolean {
        return this.paymentSession?.gateway === 'ryftpay'
    }

    get shouldCollectCustomerEmail(): boolean {
        return this.bookingDetails !== undefined
    }

    get paymentTerminology() {
        const availability = this.rootFormGroup.control
            .get('BookingSlot')
            ?.get('availability')
            ?.value as Availability | null
        return bookingPaymentTerminologyForVenueId(
            this.venue?.id ?? availability?.venue.id
        )
    }

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private loggingService: LoggingService,
        private rootFormGroup: FormGroupDirective,
        private paymentsService: PaymentsService
    ) { }

    ngOnInit() {
        this.billingEmailAddressControl.setValue(this.bookingDetails?.bookingEmailAddress ?? '')
        this.setUpDepositDetails()
    }

    ngOnDestroy() {
        this.destroyed = true
        this.paymentSession?.destroy()
        this.paymentSession = null
    }

    onBackSelected() {
        this.backSelected.emit()
    }

    async onBookSelected() {
        this.isSaving = true
        this.paymentStarted.emit(this.paymentIntent?.id)
        try {
            await this.savePaymentDetails()
            this.bookSelected.emit()
        } catch (error) {
            this.paymentFailed.emit()
            if (error instanceof PaymentSessionValidationError) {
                return
            }
            const defaultErrorMessage = 'There was an error taking your payment. ' +
                'Please refresh the page and try again.'
            const message = error instanceof Error || error instanceof HttpErrorResponse
                ? error.message
                : defaultErrorMessage
            this.loggingService.noticeError(error instanceof Error ? error : new Error(message))
            if (error instanceof HttpErrorResponse && error.status === 404) {
                this.bookingSlotUnavailableWarning = 'Sorry, this booking slot is no longer available. Please select another time.'
            } else {
                this.formError = message
            }
        } finally {
            this.isSaving = false
        }
    }

    clearFormErrors() {
        this.formError = null
        this.bookingSlotUnavailableWarning = null
    }

    private setUpDepositDetails() {
        if (this.venue !== undefined && this.bookingDetails !== undefined) {
            this.setUpDepositDetailsFromBookingDetails(this.venue, this.bookingDetails)
        } else if (this.customerDepositAmount !== null) {
            this.setUpDepositDetailsFromCustomerDeposit()
        } else {
            this.setUpDepositDetailsFromForm()
        }
    }

    private setUpDepositDetailsFromBookingDetails(venue: Venue, bookingDetails: BookingDetails) {
        const depositAmount = bookingDetails.upfrontAmountOutstanding
        const depositCurrencyCode = bookingDetails.depositCurrencyCode ?? bookingDetails.sale?.order.currencyCode ?? null
        if (depositCurrencyCode === null) {
            return
        }
        this.amount = DineroFactory({
            amount: depositAmount,
            currency: depositCurrencyCode as Currency,
        })
        this.refundCutOffTime = bookingDetails.depositRefundCutOffTime
        this.mountSetUpPendingBookingPaymentDetailsElement(venue, bookingDetails)
            .catch(error => {
                if (error instanceof HttpErrorResponse && error.status === 404) {
                    this.bookingSlotUnavailableWarning = 'Sorry, this booking slot is no longer available. Please select another time.'
                } else {
                    this.formError =
                        'There was an error setting up your booking. ' +
                        'Please refresh the page and try again.'
                }
            })
    }

    private setUpDepositDetailsFromCustomerDeposit() {
        const bookingSlotForm = this.rootFormGroup.control.get('BookingSlot')
        const availabilityControl = bookingSlotForm?.get('availability')
        const availability = availabilityControl?.value as Availability | null
        if (availability === null) {
            return
        }
        const venue: Venue = this.venue ?? availability?.venue
        if (venue === null) {
            return
        }
        const areaId: string = bookingSlotForm?.get('selectedAreaId')?.value as string
        if (areaId === null) {
            return
        }
        const partySize = bookingSlotForm?.get('partySize')?.value as number
        const depositAmount = this.customerDepositIsPerCover
            ? this.customerDepositAmount! * partySize
            : this.customerDepositAmount!
        this.amount = DineroFactory({
            amount: depositAmount,
            currency: venue.currencyCode as Currency,
        })
        this.refundCutOffHours = null
        const bookingRequestId = uuidv4()
        bookingSlotForm?.patchValue({ 'bookingRequestId': bookingRequestId })
        const factory = new BookingRequestFactory(
            this.rootFormGroup.control,
            this.utmSource ?? null,
            this.utmMedium ?? null,
            this.utmCampaign ?? null
        )
        const bookingRequest = factory.make()
        this.mountSetUpPaymentDetailsElement(venue, areaId, bookingRequest)
            .catch(error => {
                if (error instanceof HttpErrorResponse && error.status === 404) {
                    this.bookingSlotUnavailableWarning = 'Sorry, this booking slot is no longer available. Please select another time.'
                } else {
                    this.formError =
                        'There was an error setting up your booking. ' +
                        'Please refresh the page and try again.'
                }
            })
    }

    private setUpDepositDetailsFromForm() {
        const bookingSlotForm = this.rootFormGroup.control.get('BookingSlot')
        const availabilityControl = bookingSlotForm?.get('availability')
        const availability = availabilityControl?.value as Availability | null
        if (availability === null) {
            return
        }
        const venue: Venue = this.venue
            ?? availability?.venue
        if (venue === null) {
            return
        }
        const areaId: string = bookingSlotForm?.get('selectedAreaId')?.value as string
        if (areaId === null) {
            return
        }
        let bookingSlot: BookingSlot = bookingSlotForm?.get('bookingSlot')?.value
        if (bookingSlot === undefined) {
            const ngbDate = bookingSlotForm?.get('date')?.value
            const ngbTime = bookingSlotForm?.get('time')?.value
            const date = this.ngbDateAdapter.toModel(ngbDate)!
            const time = this.ngbTimeAdapter.toModel(ngbTime)!
            const dateTime = availability.getDateTimeForBookingSlotDateAndTime(
                date,
                time
            )
            bookingSlot = new BookingSlot(dateTime)
        }
        if (bookingSlot === null) {
            return
        }
        const bookingSlotOption = availability
            .reservableOptionWithConfiguration(
                areaId,
                bookingSlotForm?.get('partySize')?.value,
                bookingSlotForm?.get('selectedReasonId')?.value,
                bookingSlotForm?.get('selectedEventId')?.value,
                bookingSlot.dateTime
            )
        if (bookingSlotOption === null) {
            return
        }
        const depositAmount = bookingSlotOption?.depositChargeAmount
        if (depositAmount === null) {
            return
        }
        this.amount = DineroFactory({
            amount: depositAmount!,
            currency: venue.currencyCode as Currency,
        })
        this.refundCutOffHours = bookingSlotOption?.depositRefundCutOffHours ?? null
        const bookingRequestId = uuidv4()
        bookingSlotForm?.patchValue({ 'bookingRequestId': bookingRequestId })
        const factory = new BookingRequestFactory(this.rootFormGroup.control, this.utmSource ?? null, this.utmMedium ?? null, this.utmCampaign ?? null)
        const bookingRequest = factory.make()
        this.mountSetUpPaymentDetailsElement(venue, areaId, bookingRequest)
            .catch(error => {
                if (error instanceof HttpErrorResponse && error.status === 404) {
                    this.bookingSlotUnavailableWarning = 'Sorry, this booking slot is no longer available. Please select another time.'
                } else {
                    this.formError =
                        'There was an error setting up your booking. ' +
                        'Please refresh the page and try again.'
                }
            })
    }

    private async mountSetUpPaymentDetailsElement(
        venue: Venue,
        areaId: string,
        bookingRequest: BookingRequest
    ) {
        const payment = await this.paymentsService.makePaymentIntentClientSecret(
            venue.organisationId,
            venue.businessId,
            venue.id,
            areaId,
            bookingRequest
        )
        if (payment === null) throw new Error('Payment details were not returned')
        await this.startPaymentSession(venue, payment)
    }

    private async mountSetUpPendingBookingPaymentDetailsElement(
        venue: Venue,
        bookingDetails: BookingDetails
    ) {
        const payment = await this.paymentsService.makePendingBookingPaymentIntentClientSecret(
            venue.organisationId,
            venue.businessId,
            bookingDetails
        )
        await this.startPaymentSession(venue, payment)
    }

    private async startPaymentSession(
        venue: Venue,
        intent: PaymentIntent
    ) {
        if (this.destroyed) {
            return
        }
        this.cardDetailsAreValid = false
        this.paymentFormIsReady = false
        this.changeDetectorRef.detectChanges()
        await Promise.resolve()
        if (this.destroyed) {
            return
        }
        const paymentSession = await this.paymentsService.createPaymentSession(venue, {
            intent,
            usage: 'payment',
            cardFormSelector: '#payment-card-form',
            requiresBillingEmail: this.shouldCollectCustomerEmail,
            getBillingEmailAddress: this.shouldCollectCustomerEmail
                ? () => this.validBillingEmailAddress()
                : undefined,
            applePayWalletButtonSelector: '#payment-apple-wallet-button',
            googlePayWalletButtonSelector: '#payment-google-wallet-button',
        })
        if (this.destroyed) {
            paymentSession.destroy()
            return
        }
        this.paymentSession = paymentSession
        this.paymentIntent = intent
        this.walletPaymentAvailable = paymentSession.walletPaymentAvailable
        this.paymentFormIsReady = true
        this.changeDetectorRef.detectChanges()
        paymentSession.setOnValidityChange((isValid) => this.updateCardValidationState(isValid))
        paymentSession.setOnWalletConfirmed(() => this.onBookSelected())
        paymentSession.setOnError(() => {
            this.cardDetailsAreValid = false
            this.changeDetectorRef.detectChanges()
        })
    }

    private async savePaymentDetails() {
        const billingEmailAddress = this.validBillingEmailAddress()
        if (this.shouldCollectCustomerEmail && billingEmailAddress === undefined) {
            throw new PaymentSessionValidationError('Please enter a valid email address.')
        }
        if (!this.paymentSession) {
            throw new Error('Please refresh the page and try again')
        }
        const paymentIntentId = await this.paymentSession.confirm(billingEmailAddress)
        const formName = FormStep[FormStep.Deposit]
        const form = this.rootFormGroup.control.get(formName) as FormGroup
        form.get('paymentIntentId')?.setValue(paymentIntentId)
    }

    private validBillingEmailAddress(): string | undefined {
        if (!this.shouldCollectCustomerEmail) {
            return undefined
        }
        this.billingEmailAddressControl.markAsTouched()
        this.changeDetectorRef.detectChanges()
        if (this.billingEmailAddressControl.invalid) {
            return undefined
        }
        return this.billingEmailAddressControl.value.trim()
    }

    private updateCardValidationState(isValid: boolean) {
        this.cardDetailsAreValid = isValid
        this.changeDetectorRef.detectChanges()
    }
}
